/* eslint-disable no-nested-ternary */
import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import Color from 'color';
import PropTypes from 'prop-types';
// Core components
import AppLoader from 'components/core/AppLoader';
import Avatar from 'components/core/Avatar';
import ListItem from 'components/core/ListItem';
import Paper from 'components/core/Paper';
import Skeleton from 'components/core/Skeleton';
import Switch from 'components/core/Switch';
// Color
import { iconPalette, statusPalette } from 'components/core/colors';
// Custom
import { CollapseIconButton } from 'components/buttons/IconButtons';
import AppIcon from 'components/icons/AppIcon';

const NestedInformationItem = ({
  icon,
  iconColor,
  label,
  value,
  rightButton,
  deprecated,
}) => (
  <div
    className="d-flex align-items-center information-nested-item"
    style={deprecated ? { backgroundColor: statusPalette.deprecated } : null}
  >
    {icon ? (
      <div className="px-3 pt-1">
        <AppIcon id={icon} color={iconColor} />
      </div>
    )
      : <div className="information-no-icon" />}
    <div className="d-flex flex-nowrap justify-content-between align-items-center w-100">
      <div>
        <div className="row information-label">
          {deprecated && (
            <div className="mr-2">
              <strong>
                <LocalizedMessage id="info.deprecated" />
              </strong>
            </div>
          )}
          {label}
        </div>
        <div className="row information-value">
          {value}
        </div>
      </div>
      <div className="d-flex align-items-center pr-1">
        {rightButton}
      </div>
    </div>
  </div>
);

NestedInformationItem.propTypes = {
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  rightButton: PropTypes.element,
  deprecated: PropTypes.bool,
};

NestedInformationItem.defaultProps = {
  icon: null,
  iconColor: iconPalette.base,
  rightButton: null,
  deprecated: false,
};

const InformationItem = ({
  light, withShadow, initiallyOpen, loading, color, lightMode, icon, iconColor, customIcon, label, value, gender,
  withAvatar, nestedItems, toggle, rightButton, rightIcon, onClick, noMargin,
}) => {
  if (light) {
    //
    return (
      <ListItem
        button={false}
        primaryText={value ? (
          <div className="text-truncate" style={{ whiteSpace: 'normal' }}>
            {value}
          </div>
        ) : (
          <div style={{ padding: '4px 0' }}>
            <Skeleton style={{ height: 16 }} />
          </div>
        )}
        leftItem={
          loading ? (
            <AppLoader size={24} />
          ) : (
            icon ? <AppIcon id={icon} color={iconColor} /> : customIcon
          )
        }
        rightItem={rightIcon}
      />
    );
  }

  const [open, setOpen] = React.useState(initiallyOpen);
  const [hover, setHover] = React.useState(false);

  const handleCollapseClick = () => {
    setOpen(!open);
  };
  const handleHover = (hoverArg) => {
    setHover(hoverArg);
  };

  // Set style if color is defined
  const usedColor = color || (lightMode ? '#fff' : null);
  const style = {};
  if (usedColor) {
    if (!hover) {
      style.backgroundColor = Color(usedColor).alpha(0.65);
    } else {
      style.backgroundColor = Color(usedColor).alpha(0.85);
    }
  }

  return (
    <div
      className={
        classnames({
          'information-item-container': !noMargin,
          'information-item-selection': !_isNil(onClick),
        })
      }
      role="presentation"
      onClick={onClick}
    >
      <Paper elevation={withShadow ? 1 : 0} square>
        <div
          className="d-flex align-items-center information-item"
          style={style}
          onMouseEnter={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
        >
          {loading ? (
            <div className="px-3">
              <AppLoader size={24} />
            </div>
          ) : (
            (icon || customIcon) ? (
              <div className="px-3">
                {withAvatar ? (
                  <Avatar
                    size={32}
                    primary={withAvatar !== 'colorized'}
                    backgroundColor={withAvatar === 'colorized' ? iconColor : null}
                  >
                    {customIcon || <AppIcon id={icon} />}
                  </Avatar>
                ) : (
                  <div className="pt-1">
                    {customIcon ? (
                      <div style={{ width: 24, height: 24 }} className="text-center">
                        {customIcon}
                      </div>
                    ) : <AppIcon id={icon} color={iconColor} />}
                  </div>
                )}
              </div>
            ) : (
              <div className="information-no-icon" />
            )
          )}
          <div
            className={
              classnames('d-flex flex-nowrap justify-content-between align-items-center w-100', {
                'pr-3': (!toggle && !nestedItems && !rightButton),
                'pr-1': (nestedItems || rightButton),
              })
            }
          >
            <div>
              <div className="row information-label">
                {label || ' '}
              </div>
              <div className="row information-value">
                {value || <LocalizedMessage id="info.unknown" values={{ gender }} />}
              </div>
            </div>
            <div className="d-flex align-items-center">
              {rightIcon && (
                <div className="d-flex align-items-center">
                  {rightIcon}
                </div>
              )}
              {toggle && (
                <div className="ml-2">
                  <Switch checked={toggle.toggled} edge="end" onChange={toggle.onToggle} />
                </div>
              )}
              {!toggle && rightButton}
              {!toggle && !rightButton && nestedItems && (
                <CollapseIconButton
                  open={open}
                  onClick={handleCollapseClick}
                />
              )}
            </div>
          </div>
        </div>
      </Paper>
      {nestedItems && open && (
        <div className="information-nested-items">
          {_map(nestedItems, (itemProps) => (
            <NestedInformationItem {...itemProps} />
          ))}
        </div>
      )}
    </div>
  );
};

InformationItem.propTypes = {
  light: PropTypes.bool,
  withShadow: PropTypes.bool,
  loading: PropTypes.bool,
  color: PropTypes.string,
  lightMode: PropTypes.bool,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  customIcon: PropTypes.element,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  gender: PropTypes.oneOf(['male', 'female']),
  initiallyOpen: PropTypes.bool,
  withAvatar: PropTypes.oneOf([true, false, 'colorized']),
  nestedItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    rightButton: PropTypes.element,
  })),
  toggle: PropTypes.shape({
    toggled: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
  }),
  rightButton: PropTypes.element,
  rightIcon: PropTypes.element,
  noMargin: PropTypes.bool,
  onClick: PropTypes.func,
};

InformationItem.defaultProps = {
  light: false,
  withShadow: false,
  loading: false,
  color: null,
  lightMode: false,
  icon: null,
  iconColor: iconPalette.base,
  customIcon: null,
  label: null,
  value: null,
  gender: 'male',
  initiallyOpen: false,
  withAvatar: false,
  nestedItems: null,
  toggle: null,
  rightButton: null,
  rightIcon: null,
  onClick: null,
  noMargin: false,
};

module.exports = InformationItem;



// WEBPACK FOOTER //
// ./components/items/InformationItem.js